import { useUser } from "@clerk/react";
import { Link as RouterLink } from "react-router";
import { ArrowRightIcon } from "@phosphor-icons/react";
import { Box, Button, Chip, Container, Stack, Typography } from "@mui/material";
import { AMBER, warmGlowBackground } from "@scoutingops/ui";

import { paths } from "@/paths";

const HUB_URL = (import.meta.env.VITE_HUB_URL ?? "https://scoutingops.com") as string;

export default function HeroSection() {
  const { isSignedIn } = useUser();

  return (
    <Box
      component="section"
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        overflow: "hidden",
        position: "relative",
        py: { xs: 10, md: 16 },
        textAlign: "center",
      }}
    >
      {/* Warm brand glow — green → amber → ember, echoing the logo palette */}
      <Box
        aria-hidden
        sx={{ background: warmGlowBackground, inset: 0, pointerEvents: "none", position: "absolute" }}
      />
      {/* Dot grid fading in from the top, tinted with the app's primary green */}
      <Box
        aria-hidden
        sx={{
          backgroundImage:
            "radial-gradient(circle, rgba(var(--mui-palette-primary-mainChannel) / 0.18) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
          inset: 0,
          maskImage: "radial-gradient(ellipse 70% 60% at 50% 0%, black 0%, transparent 100%)",
          pointerEvents: "none",
          position: "absolute",
        }}
      />

      <Container maxWidth="md" sx={{ position: "relative" }}>
        <Stack spacing={4} sx={{ alignItems: "center" }}>
          <Chip
            label="Scouting Ops Site"
            size="small"
            variant="outlined"
            sx={{ borderColor: "primary.main", color: "primary.main", fontWeight: 700, px: 0.5 }}
          />

          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 900,
              fontSize: { xs: "2.25rem", sm: "2.75rem", md: "3.5rem" },
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
            }}
          >
            Your unit&apos;s public home,{" "}
            <Box
              component="span"
              sx={{
                color: "primary.main",
                // `inline` + cloned box decoration so the amber underline hugs
                // each line of the phrase when it wraps, rather than painting one
                // full-width bar across the whole inline-block box.
                display: "inline",
                WebkitBoxDecorationBreak: "clone",
                boxDecorationBreak: "clone",
                backgroundImage: `linear-gradient(${AMBER}, ${AMBER})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "0 92%",
                backgroundSize: "100% 0.12em",
                pb: "0.02em",
              }}
            >
              built in minutes.
            </Box>
          </Typography>

          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ fontWeight: 400, lineHeight: 1.6, maxWidth: 600 }}
          >
            Site is the public front door for the Scouting Ops platform — a no-code website that
            pulls in your unit&apos;s published events and stays current automatically, so no one
            has to remember to update it.
          </Typography>

          <Stack direction="row" spacing={2} sx={{ justifyContent: "center", flexWrap: "wrap", gap: 2 }}>
            {isSignedIn ? (
              <Button
                component={RouterLink}
                to={paths.studio}
                variant="contained"
                size="large"
                endIcon={<ArrowRightIcon size={18} />}
                sx={{ fontWeight: 700 }}
              >
                Open Studio
              </Button>
            ) : (
              <>
                <Button
                  component="a"
                  href={`${HUB_URL}/auth/sign-in`}
                  variant="contained"
                  size="large"
                  endIcon={<ArrowRightIcon size={18} />}
                  sx={{ fontWeight: 700 }}
                >
                  Get Started
                </Button>
                <Button
                  component="a"
                  href={`${HUB_URL}/auth/waitlist`}
                  variant="outlined"
                  size="large"
                >
                  Join the Waitlist
                </Button>
              </>
            )}
          </Stack>

          <Typography
            color="text.disabled"
            variant="caption"
            sx={{ fontSize: "0.8rem", letterSpacing: "0.02em" }}
          >
            Free to get started · No credit card required · Built for youth programs
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}
